import { Route, Ban, Construction, ParkingCircleOff, Check, X, Undo2, Waypoints, ArrowLeftRight, Ruler, BoxSelect, Wrench, Spline, Warehouse } from "lucide-react";
import { KINDS } from "@/lib/constants";
import { SIGNS } from "@/components/map/SignObjects";
import { ObjectsMenu } from "@/components/map/ObjectsMenu";

// «Corte»: dos clics (inicio y final) y la app sigue la calle; «Tramo»: el mismo corte dibujado a mano punto a punto
const TOOLS = [
  { kind: "route", icon: Route, testid: "map-draw-route-button" },
  { kind: "corte", icon: Ban, testid: "map-add-cut-point-marker-button", label: "Corte", title: "Corte de calle: pulse el inicio y el final", color: "#ef4444" },
  { kind: "street_cut", icon: Construction, testid: "map-draw-cut-polyline-button", label: "Tramo", title: "Corte dibujado a mano, punto a punto" },
  { kind: "no_parking", icon: ParkingCircleOff, testid: "map-draw-no-parking-polygon-button" },
  { kind: "special_path", icon: Waypoints, testid: "map-draw-special-path-button" },
  { kind: "reversed_oneway", icon: ArrowLeftRight, testid: "map-draw-reversed-oneway-button" },
  { kind: "manual_detour", icon: Spline, testid: "map-draw-manual-detour-button", title: "Desvío dibujado a mano: pulse los nodos y se ajusta a las calles" },
  { kind: "garages", icon: Warehouse, testid: "map-draw-garages-button", title: "Línea de garajes: entrada o salida prohibida, con su cartel" },
];

const CORTE_META = { label: "Corte de calle", hint: "Pulse el inicio del corte y después el final: se marca la calle entre los dos puntos. Para cerrar solo un punto, use la señal de dirección prohibida (Objetos)." };
const LINE_META = { label: "Línea de vallas", hint: "Pulse puntos a lo largo del recorrido de las vallas y pulse Terminar. El número se calcula según la separación." };

// Botón de la barra de herramientas del mapa: icono con su nombre debajo (se entiende sin pasar el ratón)
export const RailButton = ({ active, tone = "sky", icon: Icon, color, label, title, onClick, testid, children }) => (
  <button onClick={onClick} data-testid={testid} title={title || label}
    className={`w-[3.6rem] flex flex-col items-center gap-0.5 px-1 py-1.5 rounded-lg text-[0.5625rem] leading-tight font-medium transition-colors ${active ? (tone === "amber" ? "bg-amber-400 text-slate-950" : "bg-sky-500 text-white") : "text-slate-700 hover:bg-slate-100"}`}>
    {children || <Icon className="w-[1.125rem] h-[1.125rem]" style={{ color: active ? undefined : color }} />}
    <span className="text-center">{label}</span>
  </button>
);

const Sep = () => <span className="h-px w-10 bg-slate-200 my-0.5" />;

// Barra vertical a la izquierda del mapa: dibujar, objetos y herramientas
// (readOnly: solo la regla, para quien consulta sin poder editar)
export const DrawToolbar = ({ mode, setMode, measureActive, onMeasure, readOnly, canFix }) => (
  <div className="absolute top-3 left-3 z-[600] flex flex-col items-center gap-0.5 bg-white/95 backdrop-blur-md border border-slate-200 rounded-xl p-1 shadow-sm max-h-[calc(100%-1.5rem)] overflow-y-auto overscroll-contain ap-sin-barra" data-testid="map-draw-toolbar">
    {!readOnly && (
      <>
        {TOOLS.map(({ kind, icon, testid, label, title, color }) => (
          <RailButton key={kind} testid={testid} icon={icon} color={color || KINDS[kind].color} label={label || KINDS[kind].short} title={title || KINDS[kind].label}
            active={mode === kind} onClick={() => setMode(mode === kind ? null : kind)} />
        ))}
        <Sep />
        <ObjectsMenu mode={mode} setMode={setMode} />
        <Sep />
      </>
    )}
    <RailButton testid="map-measure-button" icon={Ruler} label="Medir" title="Medir distancia y vallas" active={measureActive} onClick={onMeasure} />
    {!readOnly && (
      <RailButton testid="map-box-template-button" icon={BoxSelect} label="Plantilla" title="Guardar como plantilla lo que quede dentro de un recuadro"
        tone="amber" active={mode === "box"} onClick={() => setMode(mode === "box" ? null : "box")} />
    )}
    {canFix && (
      <RailButton testid="map-fix-streets-button" icon={Wrench} label="Corregir calle" title="Corregir el callejero del concello (sentidos, nombres, giros, calles cerradas)"
        tone="amber" active={mode === "corregir"} onClick={() => setMode(mode === "corregir" ? null : "corregir")} />
    )}
  </div>
);

// Instrucciones de la herramienta activa: siempre en el mismo sitio, abajo en el centro del mapa
export const ModeHint = ({ mode, draft, onFinish, onCancel, onUndo }) => {
  if (mode?.startsWith("sign:")) {
    return (
      <div className="map-hint border-amber-400/50" data-testid="map-sign-hint">
        <div className="min-w-0"><div className="text-xs text-amber-700 font-semibold">{SIGNS[mode.slice(5)].label}</div><div className="text-xs text-slate-500">{KINDS.sign.hint}</div></div>
        <button onClick={onCancel} data-testid="map-sign-cancel-button" className="map-hint-btn"><Check className="w-3.5 h-3.5" /> Terminar</button>
      </div>
    );
  }
  const meta = KINDS[mode] || (mode === "sign_line" ? LINE_META : mode === "corte" ? CORTE_META : null);
  if (!meta || mode === "sign") return null;
  return (
    <div className="map-hint border-sky-500/40" data-testid="map-draw-hint">
      <div className="min-w-0"><div className="text-xs text-sky-700 font-semibold">{meta.label}</div><div className="text-xs text-slate-500">{meta.hint}</div></div>
      <div className="flex gap-1.5 shrink-0">
        {mode === "corte" && <span className="text-xs font-mono text-slate-500 self-center">{draft.length ? "Falta el final" : "Inicio"}</span>}
        {mode !== "cut_point" && mode !== "corte" && (
          <>
            <button onClick={onFinish} disabled={draft.length < (mode === "no_parking" ? 3 : 2)} data-testid="map-draw-finish-button"
              className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-500 text-slate-950 text-xs font-semibold disabled:opacity-40">
              <Check className="w-3.5 h-3.5" /> Terminar ({draft.length})
            </button>
            <button onClick={onUndo} disabled={!draft.length} data-testid="map-draw-undo-button" title="Quitar el último punto" className="map-hint-btn disabled:opacity-40"><Undo2 className="w-3.5 h-3.5" /></button>
          </>
        )}
        <button onClick={onCancel} data-testid="map-draw-cancel-button" title="Cancelar" className="map-hint-btn"><X className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
};
